/* eslint-disable*/

import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import { DefaultTable } from 'src/components';

import { useTypedSelector } from 'src/hooks/useTypedSelector';

import { AlertAdapter, RegularButton } from 'pg-ui-library';
import { EmployeeReviewType } from 'src/services/approvals.service';

import { DependentsApprovalModal } from 'src/components/dependents-approval-modal';
import { Container } from './styles';

import { useSetCurrentEmployeeReviewType, useSetReviewModalIsOpen } from '../employee-review-changes.context';
import { usePendingsQuery } from './queries/pendings.query';
import { DateTime } from 'luxon';
import { string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useBulkApprove } from './mutations/bulk-approve.mutation';

export const EmployeeReviewListTable: React.FC<{
  isDependentOpen?: boolean;
  searchTerm: string;
  startDate: string;
  endDate: string;
  status: string;
  employmentType: string;
  employeeCompany: string;
  changeType: string;
  EmploymentStatus: string;
  columns: TableColumn<EmployeeReviewType>[];
  selectedTab: string;
}> = ({
  isDependentOpen,
  searchTerm,
  columns,
  startDate,
  endDate,
  status,
  employmentType,
  employeeCompany,
  changeType,
  EmploymentStatus,
  selectedTab,
}) => {
  const { config } = useTypedSelector(state => state.config.data);
  const companyId = config?.selectedCompanyId;
  const { data: companies } = useTypedSelector(state => state.companies);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [selectedRows, setSelectedRows] = useState<EmployeeReviewType[]>([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const { t } = useTranslation();
  const { isFetching, data, refetch } = usePendingsQuery({
    page,
    limit,
    sortBy,
    sortDirection,
    search: searchTerm,
    companyId: companyId ?? '',
    startDate: DateTime.fromISO(startDate).toFormat('yyyy-MM-dd'),
    endDate: DateTime.fromISO(endDate).toFormat('yyyy-MM-dd'),
    status,
    employmentType,
    changeType,
    employeeCompany,
    EmploymentStatus,
    selectedTab,
  });
  const bulkApproveMutation = useBulkApprove();

  const customStyles = {
    headRow: {
      style: {
        border: 'none',
      },
    },
    headCells: {
      style: {
        color: '#9399B2',
        font: 'normal normal bold 16px Biotif',
      },
    },
    rows: {
      style: {
        font: 'normal normal normal 14px Biotif',
        color: '#1B1D35',
        borderRadius: '5px',
        '&:not(:last-of-type)': {
          borderBottomWidth: '0px',
        },
      },
      stripedStyle: {
        color: '#1B1D35',
        backgroundColor: '#F3F4F8',
      },
      highlightOnHoverStyle: {
        backgroundColor: 'rgb(230, 244, 244)',
        borderBottomColor: '#FFFFFF',
        borderRadius: '25px',
        outline: '1px solid #FFFFFF',
      },
    },
    cells: {
      style: {
        '&:nth-of-type(5)': {
          textTransform: 'uppercase' as unknown as 'uppercase',
        },
        '&:nth-of-type(6)': {
          flex: '1', // override the cell padding for data cells
        },
      },
    },
    pagination: {
      style: {
        border: 'none',
        svg: {
          fill: 'rgba(0,0,0,0.54)',
        },
      },
    },
  };

  useEffect(() => {
    refetch();
  }, [searchTerm, page, limit, sortBy, sortDirection, companyId, isDependentOpen, startDate, endDate, selectedTab]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerRowsChange = async (newLimit: number, newPage: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const handleSort = async (column: TableColumn<EmployeeReviewType>, newSortDirection: SortOrder) => {
    setSortBy(column.sortField as unknown as string);
    setSortDirection(newSortDirection);
  };

  const handleSelection = (selected: { selectedRows: EmployeeReviewType[] }) => {
    setSelectedRows(selected.selectedRows);
  };

  const isRowDisabled = (row: EmployeeReviewType) => {
    // Disable checkbox for rows that are already approved
    return (
      row.approvalStatus === 'approved' ||
      row.approvalStatus === 'declined' ||
      row.requestStatus === 'APPROVED' ||
      row.requestStatus === 'DECLINED'
    );
  };

  const bulkApprove = async () => {
    const benefitSummaryIds = selectedRows.filter(e => e.summaryId as string).map(e => e.summaryId as string); // benefits
    const contactApprovalIds = selectedRows.filter(e => e.id).map(e => e.id); //address changes
    const cpn = companyId as string;
    const bulkApproveObj = {
      benefitSummaryIds,
      contactApprovalIds,
      companyId: cpn,
    };
    try {
      await bulkApproveMutation.mutateAsync(bulkApproveObj);
      setSelectedRows([]);
      setClearSelectedRows(prev => !prev); // Toggle to trigger clear
      refetch();
    } catch (error) {
      AlertAdapter('Error during fetching data', 'error');
    }
  };

  return (
    <Container style={{ marginTop: '-30px' }}>
      <RegularButton
        style={{ marginTop: '-90px' }}
        onClick={bulkApprove}
        disabled={selectedRows.length <= 0 || bulkApproveMutation.isLoading}
      >
        Approve All
      </RegularButton>
      <DefaultTable
        columns={columns}
        data={data?.items || []}
        customStyles={customStyles}
        progressPending={isFetching || bulkApproveMutation.isLoading}
        pagination
        paginationServer
        paginationTotalRows={data?.meta.totalItems}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
        selectableRows
        selectableRowDisabled={isRowDisabled}
        clearSelectedRows={clearSelectedRows}
        onSelectedRowsChange={handleSelection}
      />
    </Container>
  );
};
